/**
 * Settings Dashboard Page
 * Provides navigation sidebar on left and content area on right
 * Contains: General, Members, Usage, Custom Simulations sections
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
    ArrowLeft, 
    Building2, 
    Users, 
    BarChart3, 
    Layers, 
    Settings,
    ChevronRight
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { OrganizationSettingsGeneral } from './settings/OrganizationSettingsGeneral';
import { OrganizationSettingsMembers } from './settings/OrganizationSettingsMembers';
import { OrganizationSettingsUsage } from './settings/OrganizationSettingsUsage';
import { OrganizationSettingsSimulations } from './settings/OrganizationSettingsSimulations';

type SettingsSection = 'general' | 'members' | 'usage' | 'simulations';

interface NavItem {
    id: SettingsSection;
    label: string;
    icon: React.ElementType;
    description: string;
    adminOnly?: boolean;
}

const navItems: NavItem[] = [
    {
        id: 'general',
        label: 'General',
        icon: Building2,
        description: 'Organization name and details',
    },
    {
        id: 'members',
        label: 'Team Members',
        icon: Users,
        description: 'Manage team members and roles',
        adminOnly: true,
    },
    {
        id: 'usage',
        label: 'Usage & Analytics',
        icon: BarChart3,
        description: 'View usage statistics and metrics',
    },
    {
        id: 'simulations',
        label: 'Custom Simulations',
        icon: Layers,
        description: 'Manage custom simulations',
    },
];

export function SettingsPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const { currentOrg, canManageOrg, orgRole } = useAuth();
    
    const getInitialSection = (): SettingsSection => {
        const path = location.pathname;
        if (path.includes('/members')) return 'members';
        if (path.includes('/usage')) return 'usage';
        if (path.includes('/simulations')) return 'simulations';
        return 'general';
    };
    
    const [activeSection, setActiveSection] = useState<SettingsSection>(getInitialSection());

    useEffect(() => {
        const basePath = '/app/settings';
        const newPath = activeSection === 'general' ? basePath : `${basePath}/${activeSection}`;
        if (location.pathname !== newPath) {
            navigate(newPath, { replace: true });
        }
    }, [activeSection, navigate, location.pathname]);

    const handleBack = () => {
        navigate('/app');
    };

    const handleSectionChange = (section: SettingsSection) => {
        setActiveSection(section);
    };

    const getRoleBadgeColor = () => {
        switch (orgRole) {
            case 'org:owner':
                return 'bg-purple-100 text-purple-700';
            case 'org:admin':
                return 'bg-blue-100 text-blue-700';
            default:
                return 'bg-slate-100 text-slate-700';
        }
    };

    const getRoleLabel = () => {
        switch (orgRole) {
            case 'org:owner':
                return 'Owner';
            case 'org:admin':
                return 'Admin';
            default:
                return 'Member';
        }
    };

    const renderContent = () => {
        switch (activeSection) {
            case 'general':
                return <OrganizationSettingsGeneral />;
            case 'members':
                return <OrganizationSettingsMembers />;
            case 'usage':
                return <OrganizationSettingsUsage />;
            case 'simulations':
                return <OrganizationSettingsSimulations />;
            default:
                return <OrganizationSettingsGeneral />;
        }
    };

    const visibleNavItems = navItems.filter(item => {
        if (item.adminOnly && !canManageOrg) return false;
        return true;
    });

    return (
        <div className="min-h-screen bg-slate-50">
            {/* Header */}
            <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="flex items-center h-16">
                        <button
                            onClick={handleBack}
                            className="flex items-center space-x-2 text-slate-600 hover:text-slate-900 transition-colors mr-6"
                        >
                            <ArrowLeft className="w-5 h-5" />
                            <span className="font-medium">Back</span>
                        </button>
                        
                        <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-lg flex items-center justify-center">
                                <Settings className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h1 className="text-lg font-semibold text-slate-900">Settings</h1>
                                {currentOrg && (
                                    <p className="text-sm text-slate-500">{currentOrg.name}</p>
                                )}
                            </div>
                        </div>

                        {orgRole && (
                            <span className={`ml-4 px-2.5 py-1 rounded-full text-xs font-medium ${getRoleBadgeColor()}`}>
                                {getRoleLabel()}
                            </span>
                        )}
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-6 py-8">
                <div className="flex gap-8">
                    {/* Sidebar Navigation */}
                    <aside className="w-72 flex-shrink-0">
                        <nav className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                            <div className="p-4 border-b border-slate-100">
                                <h2 className="text-sm font-semibold text-slate-900">Settings</h2>
                                <p className="text-xs text-slate-500 mt-1">Manage your organization</p>
                            </div>
                            <ul className="p-2">
                                {visibleNavItems.map((item) => {
                                    const Icon = item.icon;
                                    const isActive = activeSection === item.id;
                                    
                                    return (
                                        <li key={item.id}>
                                            <button
                                                onClick={() => handleSectionChange(item.id)}
                                                className={`w-full flex items-center justify-between px-3 py-3 rounded-lg transition-all duration-200 group ${
                                                    isActive 
                                                        ? 'bg-emerald-50 text-emerald-700' 
                                                        : 'text-slate-600 hover:bg-slate-50'
                                                }`}
                                            >
                                                <div className="flex items-center space-x-3">
                                                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center transition-colors ${
                                                        isActive 
                                                            ? 'bg-emerald-100' 
                                                            : 'bg-slate-100 group-hover:bg-slate-200'
                                                    }`}>
                                                        <Icon className={`w-5 h-5 ${
                                                            isActive ? 'text-emerald-600' : 'text-slate-500'
                                                        }`} />
                                                    </div>
                                                    <div className="text-left">
                                                        <div className={`text-sm font-medium ${
                                                            isActive ? 'text-emerald-700' : 'text-slate-700'
                                                        }`}>
                                                            {item.label}
                                                        </div>
                                                        <div className="text-xs text-slate-500">
                                                            {item.description}
                                                        </div>
                                                    </div>
                                                </div>
                                                <ChevronRight className={`w-4 h-4 transition-colors ${
                                                    isActive ? 'text-emerald-500' : 'text-slate-400'
                                                }`} />
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>
                        </nav>

                        {/* Quick Stats Card */}
                        {currentOrg && (
                            <div className="mt-6 bg-white rounded-xl border border-slate-200 shadow-sm p-4">
                                <h3 className="text-sm font-semibold text-slate-900 mb-3">Organization</h3>
                                <div className="space-y-3">
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm text-slate-500">Team Size</span>
                                        <span className="text-sm font-medium text-slate-900">
                                            {currentOrg.membersCount || 0} / {currentOrg.maxMembers || 5}
                                        </span>
                                    </div>
                                    <div className="w-full bg-slate-100 rounded-full h-2">
                                        <div 
                                            className="bg-emerald-500 h-2 rounded-full transition-all duration-300"
                                            style={{ 
                                                width: `${Math.min(100, ((currentOrg.membersCount || 0) / (currentOrg.maxMembers || 5)) * 100)}%` 
                                            }}
                                        />
                                    </div>
                                    <p className="text-xs text-slate-500">
                                        {(currentOrg.maxMembers || 5) - (currentOrg.membersCount || 0)} seats available
                                    </p>
                                </div>
                            </div>
                        )}
                    </aside>

                    {/* Main Content Area */}
                    <main className="flex-1 min-w-0">
                        {renderContent()}
                    </main>
                </div>
            </div>
        </div>
    );
}

export default SettingsPage;
